/* ============================================================================
   ACEPLORE · global.js — Core runtime, utilities & shared web-component base.
   Loaded first (defer). Everything attaches to window.Lumina.
   ========================================================================== */
(function () {
  'use strict';

  // Flip no-js -> js as early as possible for progressive enhancement hooks.
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const Lumina = (window.Lumina = window.Lumina || {});

  /* --------------------------------------------------------------------------
     Config injected from Liquid via <script id="lumina-config" type="application/json">
     -------------------------------------------------------------------------- */
  Lumina.config = (function () {
    const defaults = {
      routes: {},
      strings: {},
      moneyFormat: '${{amount}}',
      cartType: 'drawer',
      shopCurrency: 'USD',
      freeShippingThreshold: 0,
      features: {},
    };
    try {
      const el = document.getElementById('lumina-config');
      return Object.assign(defaults, el ? JSON.parse(el.textContent) : {});
    } catch (e) {
      console.warn('[Lumina] config parse failed', e);
      return defaults;
    }
  })();

  /* --------------------------------------------------------------------------
     Utilities
     -------------------------------------------------------------------------- */
  const utils = (Lumina.utils = {
    qs: (sel, root = document) => root.querySelector(sel),
    qsa: (sel, root = document) => Array.from(root.querySelectorAll(sel)),

    debounce(fn, wait = 250) {
      let t;
      return function (...args) {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    throttle(fn, limit = 200) {
      let waiting = false;
      return function (...args) {
        if (waiting) return;
        fn.apply(this, args);
        waiting = true;
        setTimeout(() => (waiting = false), limit);
      };
    },

    // Shopify money formatter (mirrors Liquid's money filter for common patterns).
    formatMoney(cents, format) {
      const fmt = format || Lumina.config.moneyFormat || '${{amount}}';
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const value = (cents || 0) / 100;
      const formatWith = (decimals, thousands = ',', decimalMark = '.') => {
        const n = (Math.round(value * 100) / 100).toFixed(decimals);
        const parts = n.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return decimals ? parts.join(decimalMark) : parts[0];
      };
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => {
        switch (name) {
          case 'amount': return formatWith(2);
          case 'amount_no_decimals': return formatWith(0);
          case 'amount_with_comma_separator': return formatWith(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return formatWith(0, '.', ',');
          case 'amount_with_space_separator': return formatWith(2, ' ', ',');
          default: return formatWith(2);
        }
      });
    },

    async fetchJSON(url, options = {}) {
      // Every JSON call gets a timeout so a stalled upstream (proxy, LLM,
      // slow app) can never leave the UI spinning forever. Pass timeout: 0
      // (or your own signal) to opt out.
      const { timeout = 20000, ...opts } = options;
      const ctrl = !opts.signal && timeout ? new AbortController() : null;
      const timer = ctrl ? setTimeout(() => ctrl.abort(new DOMException('Request timed out', 'TimeoutError')), timeout) : null;
      let res;
      try {
        res = await fetch(url, {
          ...opts,
          signal: opts.signal || (ctrl ? ctrl.signal : undefined),
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(opts.headers || {}),
          },
        });
      } finally {
        if (timer) clearTimeout(timer);
      }
      const text = await res.text();
      let data;
      try { data = text ? JSON.parse(text) : {}; } catch (_) { data = { raw: text }; }
      if (!res.ok) {
        const err = new Error(data.description || data.message || `Request failed (${res.status})`);
        err.status = res.status;
        err.data = data;
        throw err;
      }
      return data;
    },

    // Fetch one or more sections via the Section Rendering API.
    async fetchSections(sections, url = window.location.pathname) {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('sections', [].concat(sections).join(','));
      const res = await fetch(u.toString());
      return res.json();
    },

    getFocusable(root) {
      return utils.qsa(
        'a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, details, [tabindex]:not([tabindex="-1"])',
        root
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    },

    trapFocus(container, opener) {
      const initial = utils.getFocusable(container);
      (initial[0] || container).focus({ preventScroll: true });
      function onKey(e) {
        if (e.key !== 'Tab') return;
        // Recompute per keypress — dialogs load content after opening (quick
        // view, cart re-render), so a one-time snapshot pins focus to whatever
        // existed at open time and lets it escape everything added later.
        const focusables = utils.getFocusable(container);
        if (focusables.length === 0) { e.preventDefault(); return; }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && (document.activeElement === last || !container.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
      }
      container.addEventListener('keydown', onKey);
      return function release() {
        container.removeEventListener('keydown', onKey);
        if (opener && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
      };
    },

    // Ref-counted: overlays stack (quick view -> cart drawer), so the page must
    // stay locked until the LAST one closes, not the first.
    lockScroll(lock = true) {
      const b = document.body;
      utils._scrollLocks = Math.max(0, (utils._scrollLocks || 0) + (lock ? 1 : -1));
      if (lock && utils._scrollLocks === 1) {
        const sw = window.innerWidth - document.documentElement.clientWidth;
        b.style.setProperty('--lm-scrollbar-width', sw + 'px');
        b.classList.add('lm-no-scroll');
        b.style.paddingRight = sw + 'px';
      } else if (!lock && utils._scrollLocks === 0) {
        b.classList.remove('lm-no-scroll');
        b.style.paddingRight = '';
      }
    },

    storage: {
      get(key, fallback = null) {
        try { const v = localStorage.getItem(key); return v == null ? fallback : JSON.parse(v); }
        catch (_) { return fallback; }
      },
      set(key, value) {
        try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch (_) { return false; }
      },
      remove(key) { try { localStorage.removeItem(key); } catch (_) {} },
    },

    t(key, fallback) {
      return (Lumina.config.strings && Lumina.config.strings[key]) || fallback || key;
    },

    // Escape text for safe insertion (defense in depth — prefer textContent).
    // Also escapes quotes so the result is safe inside HTML attribute values.
    escapeHTML(str) {
      return String(str == null ? '' : str).replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
    },

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },
  });

  /* --------------------------------------------------------------------------
     Pub/sub event bus (decouples modules: cart, wishlist, AI, recommendations)
     -------------------------------------------------------------------------- */
  Lumina.events = (function () {
    const map = new Map();
    return {
      on(name, cb) {
        if (!map.has(name)) map.set(name, new Set());
        map.get(name).add(cb);
        return () => Lumina.events.off(name, cb);
      },
      off(name, cb) { map.has(name) && map.get(name).delete(cb); },
      emit(name, detail) {
        map.has(name) && map.get(name).forEach((cb) => { try { cb(detail); } catch (e) { console.error(e); } });
        document.dispatchEvent(new CustomEvent('lumina:' + name, { detail, bubbles: true }));
      },
    };
  })();
  // Named events used across the theme.
  Lumina.EVENTS = {
    CART_UPDATE: 'cart:update',
    CART_ADD: 'cart:add',
    CART_OPEN: 'cart:open',
    WISHLIST_UPDATE: 'wishlist:update',
    QUICKVIEW_OPEN: 'quickview:open',
    SEARCH_OPEN: 'search:open',
    AI_OPEN: 'ai:open',
    TOAST: 'toast',
  };

  /* --------------------------------------------------------------------------
     Toast notifications (single live region, a11y-friendly)
     -------------------------------------------------------------------------- */
  Lumina.toast = function (message, opts = {}) {
    let region = document.querySelector('.lm-toast-region');
    if (!region) {
      region = document.createElement('div');
      region.className = 'lm-toast-region';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    const toast = document.createElement('div');
    toast.className = 'lm-toast' + (opts.type ? ' lm-toast--' + opts.type : '');
    toast.textContent = message;
    if (opts.actionLabel && typeof opts.onAction === 'function') {
      const btn = document.createElement('button');
      btn.className = 'lm-toast__action';
      btn.textContent = opts.actionLabel;
      btn.addEventListener('click', () => { opts.onAction(); dismiss(); });
      toast.appendChild(btn);
    }
    region.appendChild(toast);
    const ttl = opts.duration || 3600;
    const timer = setTimeout(dismiss, ttl);
    function dismiss() {
      clearTimeout(timer);
      toast.style.transition = 'opacity .25s, transform .25s';
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(8px)';
      setTimeout(() => toast.remove(), 260);
    }
    return dismiss;
  };
  Lumina.events.on(Lumina.EVENTS.TOAST, (d) => Lumina.toast(d.message, d));

  /* --------------------------------------------------------------------------
     Base custom element — lifecycle helpers + auto listener cleanup
     -------------------------------------------------------------------------- */
  class LuminaElement extends HTMLElement {
    constructor() {
      super();
      this._cleanups = [];
    }
    listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this._cleanups.push(() => target.removeEventListener(type, handler, options));
    }
    sub(eventName, handler) {
      this._cleanups.push(Lumina.events.on(eventName, handler));
    }
    disconnectedCallback() {
      this._cleanups.forEach((fn) => { try { fn(); } catch (_) {} });
      this._cleanups = [];
    }
  }
  Lumina.Element = LuminaElement;

  /* --------------------------------------------------------------------------
     Scroll reveal — animates [data-reveal] / .lm-reveal into view
     -------------------------------------------------------------------------- */
  Lumina.initReveal = function (root = document) {
    // Merchant switch: "Enable scroll animations" off -> everything visible now.
    if (document.documentElement.dataset.animations === 'false') {
      utils.qsa('.lm-reveal', root).forEach((el) => el.classList.add('is-visible'));
      return;
    }
    if (utils.prefersReducedMotion() || !('IntersectionObserver' in window)) {
      utils.qsa('.lm-reveal', root).forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const delay = e.target.dataset.revealDelay;
            if (delay) e.target.style.transitionDelay = delay + 'ms';
            e.target.classList.add('is-visible');
            obs.unobserve(e.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    utils.qsa('.lm-reveal:not(.is-visible)', root).forEach((el) => io.observe(el));
  };

  /* --------------------------------------------------------------------------
     Predictive prefetch — warm product/collection pages on intent (hover/touch)
     -------------------------------------------------------------------------- */
  Lumina.initPrefetch = function () {
    if (!Lumina.config.features.prefetch) return;
    // theme.liquid ships Speculation Rules (prerender on hover) behind the same
    // setting — running this JS fallback too would double-fetch every hovered
    // link in Chromium. Keep it only where speculation rules are unsupported.
    if (window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) return;
    const seen = new Set();
    const prefetch = (href) => {
      if (!href || seen.has(href) || href.includes('/cart') || href.includes('?')) return;
      try {
        const url = new URL(href, location.origin);
        if (url.origin !== location.origin) return;
      } catch (_) { return; }
      seen.add(href);
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      document.head.appendChild(link);
    };
    const onIntent = utils.throttle((e) => {
      const a = e.target.closest('a[href]');
      if (a) prefetch(a.getAttribute('href'));
    }, 200);
    document.addEventListener('mouseover', onIntent, { passive: true });
    document.addEventListener('touchstart', onIntent, { passive: true });
  };

  /* --------------------------------------------------------------------------
     Share — site-wide delegated handler (Web Share API → clipboard fallback).
     Registered once; works for any [data-share] button on any template.
     -------------------------------------------------------------------------- */
  Lumina.initShare = function () {
    if (document.documentElement.hasAttribute('data-lm-share-init')) return;
    document.documentElement.setAttribute('data-lm-share-init', '');

    const copy = (text) => {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text).then(() => true, () => false);
      }
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return Promise.resolve(ok);
      } catch (e) {
        return Promise.resolve(false);
      }
    };

    document.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-share]');
      if (!btn) return;
      event.preventDefault();
      const url = btn.getAttribute('data-share-url') || window.location.href;
      const title = btn.getAttribute('data-share-title') || document.title;

      if (navigator.share) {
        navigator.share({ title: title, url: url }).catch(() => {});
        return;
      }
      copy(url).then((ok) => {
        if (Lumina.toast) Lumina.toast(ok ? 'Link copied to clipboard' : url, { type: ok ? 'success' : 'info' });
      });
    });
  };

  /* --------------------------------------------------------------------------
     Theme-preview guard — shop.app refuses express checkouts that originate
     from a theme-preview session (preview_theme_id param / preview_theme
     cookie), so shoppers hit Chrome's ERR_BLOCKED_BY_RESPONSE dead end.
     Flag the session on <html> so CSS can swap the wallet buttons for an
     explanatory note. Skipped in the theme editor (designMode), where
     merchants need those buttons visible to style them.
     -------------------------------------------------------------------------- */
  Lumina.initPreviewGuard = () => {
    const inPreview = () => {
      try {
        if (window.Shopify && window.Shopify.designMode) return false;
        if (/(?:^|;\s*)preview_theme=/.test(document.cookie)) return true;
        if (new URLSearchParams(window.location.search).has('preview_theme_id')) return true;
        return Boolean(document.getElementById('preview-bar-iframe'));
      } catch (e) {
        return false;
      }
    };
    const flag = () => document.documentElement.classList.add('lm-theme-preview');
    if (inPreview()) { flag(); return; }
    // The preview bar iframe is injected after load — re-check once, late.
    window.addEventListener('load', () => {
      setTimeout(() => { if (inPreview()) flag(); }, 1000);
    }, { once: true });
  };

  /* --------------------------------------------------------------------------
     Theme editor support — re-init dynamic bits when sections reload
     -------------------------------------------------------------------------- */
  function onSectionLoad(e) {
    Lumina.initReveal(e.target);
    Lumina.events.emit('section:load', { id: e.detail && e.detail.sectionId, target: e.target });
  }

  /* --------------------------------------------------------------------------
     Boot
     -------------------------------------------------------------------------- */
  function boot() {
    Lumina.initReveal();
    Lumina.initPrefetch();
    Lumina.initShare();
    Lumina.initPreviewGuard();
    document.addEventListener('shopify:section:load', onSectionLoad);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
